import type { RolePermissions } from '@platform/shared-types';

// Self-contained client for staff-service — RubyTill's OWN employees (hiring,
// onboarding workflows, panel authorization). NOT tenant employees.
// Mirrors academyApi.ts conventions — same shared `platform` cluster host
// resolution, same auth header source.
//
// NOTE the host shape: this service lives on the SHARED `platform` cluster and
// is addressed by BASE PATH (/staff), not a per-service subdomain. Do not
// "correct" this to staff.api.<apex> — that pattern is closed (AWS domain cap).

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveStaffHost(
  hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string,
): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/staff-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/staff`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/staff`;
  return `https://platform.api.dev.${apex}/staff`;
}

/** Exported so staffDocumentService.ts shares the exact same host. */
export const staffHost = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_STAFF_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveStaffHost(h, detectStage(), envBase);
};

// Same auth source as hardwareApi.ts / academyApi.ts / billingApi.ts:
// super-admin-ui stores the admin session token under 'jwtToken' and sends it
// bare (no "Bearer " prefix).
export function staffAuthHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

/**
 * Thrown instead of a plain Error when the server's JSON error body carries
 * extra structured detail (e.g. the 409 `members` list a role-delete returns
 * when staff still belong to it) — callers that care can `instanceof` this
 * and read `.body`; everyone else keeps catching a plain Error, since this
 * extends it.
 */
export class StaffApiError extends Error {
  body: any;
  status: number;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = 'StaffApiError';
    this.status = status;
    this.body = body;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${staffHost()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...staffAuthHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON body */ }
    const detail = body?.error || '';
    throw new StaffApiError(detail || `staff ${res.status}`, res.status, body);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StaffStatus = 'invited' | 'active' | 'suspended' | 'terminated';

export interface MySecurity {
  authenticatorEnabled: boolean;
  emailCodeEnabled: boolean;
  recoveryCodesRemaining: number;
  recoveryCodesCreatedAt: string | null;
  recoveryInProgress: boolean;
}

export interface SecurityEvent {
  id: string;
  at: string;
  event: string;
  /** The affected admin. Absent on role events (I3) — those carry targetLabel. */
  targetEmail?: string;
  /** The role name for role_* events. */
  targetLabel?: string;
  actorEmail?: string;
  ipAddress?: string;
  detail?: string;
}

export interface StaffMember {
  id: string;                 // Cognito sub in the PlatformAdminUserPool
  entityType: 'staff';
  email: string;
  name: string;
  roleId: string;
  title?: string;
  phone?: string;
  hireDate?: string;
  status: StaffStatus;
  terminatedAt?: string;
  terminationReason?: string;
  terminationNotes?: string;
  onboardingStatus?: 'not_started' | 'in_progress' | 'completed';
  onboardingStartedAt?: string;
  onboardingCompletedAt?: string;
  activeWorkflows?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  /** Cognito pool member with no staff row (owner / hand-created admin) — shown read-only. */
  unmanaged?: boolean;
  isOwner?: boolean;
}

export interface StaffRole {
  id: string;                 // role_<slug>
  entityType: 'role';
  name: string;
  description?: string;
  /** sectionId -> 'view' | 'edit' (@platform/shared-types RolePermissions).
   *  A granted parent section covers its children; '/staff' grants staff management. */
  permissions: RolePermissions;
  staffCount?: number;
  createdAt: string;
  updatedAt: string;
  /** Seeded roles (e.g. role_owner) — not deletable, name/description may be locked. */
  builtIn?: boolean;
  locked?: boolean;
}

export interface StaffMe {
  isRegistered: boolean;
  staff: StaffMember | null;
  role: StaffRole | null;
  /** sectionId -> 'view' | 'edit'. Empty/absent for an unregistered staff member. */
  levels: RolePermissions;
  isOwner: boolean;
  /** @deprecated use `levels` — kept only while callers migrate; equals Object.keys(levels). */
  permissions: string[];
  canManage: boolean;
  email: string;
  name: string;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const staffApi = {
  /** The caller's own profile + panel permissions — used to gate the sidebar. */
  me: () => call<StaffMe>('/me'),

  staff: {
    list: () => call<{ staff: StaffMember[]; count: number }>('/admin/staff'),
    get: (staffId: string) =>
      call<{ staff: StaffMember; role: StaffRole | null }>(`/admin/staff/${encodeURIComponent(staffId)}`),
    invite: (input: { email: string; name: string; roleId: string; title?: string; phone?: string; hireDate?: string }) =>
      call<{ staff: StaffMember; invitedViaCognito: boolean }>('/admin/staff/invite', {
        method: 'POST', body: JSON.stringify(input),
      }),
    update: (staffId: string, input: Partial<Pick<StaffMember,
      'name' | 'title' | 'phone' | 'roleId' | 'hireDate' | 'status' | 'terminationReason' | 'terminationNotes'>> & { notifyEmployee?: boolean }) =>
      call<{ staff: StaffMember }>(`/admin/staff/${encodeURIComponent(staffId)}`, {
        method: 'PUT', body: JSON.stringify(input),
      }),
    remove: (staffId: string) =>
      call<{ message: string }>(`/admin/staff/${encodeURIComponent(staffId)}`, { method: 'DELETE' }),
    resetPassword: (staffId: string) =>
      call<{ message: string; temporaryPassword: string }>(`/admin/staff/${encodeURIComponent(staffId)}/reset-password`, { method: 'POST' }),
    resendInvite: (staffId: string) =>
      call<{ message: string }>(`/admin/staff/${encodeURIComponent(staffId)}/resend-invite`, { method: 'POST' }),
  },

  /** Two-step sign-in (MFA) and recovery codes — staff-service handlers/mfa.ts. */
  security: {
    mine: () => call<MySecurity>('/me/security'),
    regenerateCodes: () => call<{ codes: string[] }>('/me/recovery-codes', { method: 'POST' }),
    /** After (re-)enrolling an authenticator: email code off, new codes issued. */
    complete: () => call<{ codes: string[] }>('/me/mfa/complete', { method: 'POST' }),
    /** userId is the Cognito sub — works for the owner/unmanaged rows too. */
    resetMfa: (userId: string) =>
      call<{ message: string; expiresAt: string }>(`/admin/staff/${encodeURIComponent(userId)}/reset-mfa`, { method: 'POST' }),
    events: (limit = 200) => call<{ events: SecurityEvent[] }>(`/admin/security-events?limit=${limit}`),
    /** UNAUTHENTICATED — email + password + one recovery code opens a recovery. */
    recover: async (input: { email: string; password: string; code: string }) => {
      const res = await fetch(`${staffHost()}/public/mfa-recovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Those details didn't match.");
      return body as { message: string; expiresInMinutes: number };
    },
  },

  roles: {
    list: () => call<{ roles: StaffRole[]; count: number }>('/admin/roles'),
    create: (input: { name: string; description?: string; permissions: RolePermissions }) =>
      call<{ role: StaffRole }>('/admin/roles', { method: 'POST', body: JSON.stringify(input) }),
    /** The server returns the saved role plus the per-section changes it recorded (for the Access log / email). */
    update: (roleId: string, input: Partial<Pick<StaffRole, 'name' | 'description' | 'permissions'>>) =>
      call<{ role: StaffRole; changes: Array<{ sectionId: string; from: string | null; to: string | null }> }>(
        `/admin/roles/${encodeURIComponent(roleId)}`,
        { method: 'PUT', body: JSON.stringify(input) },
      ),
    /** 409 (members still assigned) throws StaffApiError whose .body carries `members: [{id,name,email}]`. */
    remove: (roleId: string) =>
      call<{ message: string }>(`/admin/roles/${encodeURIComponent(roleId)}`, { method: 'DELETE' }),
  },

  uploads: {
    /** Presigned PUT into the private staff-docs bucket. Managers may pass
     *  staffId to upload on someone's behalf; everyone else signs their own. */
    sign: (input: { fileName: string; contentType: string; staffId?: string }) =>
      call<{ uploadUrl: string; key: string; fileName: string }>('/uploads/sign', {
        method: 'POST', body: JSON.stringify(input),
      }),
    download: (input: { key: string; disposition?: 'inline' | 'attachment'; fileName?: string }) =>
      call<{ url: string; key: string }>('/uploads/download', {
        method: 'POST', body: JSON.stringify(input),
      }),
    /** Browser -> S3 via the presigned URL (never through API Gateway). */
    put: async (uploadUrl: string, file: File | Blob, contentType: string): Promise<void> => {
      const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!res.ok) throw new Error(`upload failed (${res.status})`);
    },
  },
};
